import CustomInput from "@components/CustomInput/CustomInput";
import styles from "./DoctorDetailComponent.module.css";
import DetailTableComponent from "@components/DetailTableComponent/DetailTableComponent";
import { useEffect, useState } from "react";
import Button from "@components/Button";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Home } from "@mui/icons-material";
import { useNavigate } from "react-router";
import useWindowSize from "@hooks/useWindowSize";
import useDoctor from "@hooks/useDoctor";
import usePopup from "@hooks/usePopup";
import ConfirmDetailsPopup from "@components/ConfirmDetailsPopup/ConfirmDetailsPopup";
import { notify } from "src/utils/notify";
import JsonDisplay from "@components/JsonDisplay/JsonDisplay";
import React from "react";

const DoctorDetailComponent = ({ doctorData }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [doctorsArray, setDoctorsArray] = useState([]);
  const [editedDoctorDetails, setEditedDoctorDetails] = useState({});
  const { width } = useWindowSize();
  const navigate = useNavigate();
  const { handleUpdateDoctor } = useDoctor();
  const { handlePopupCenterComponentRender, handlePopupCenterOpen } =
    usePopup();

  const fieldNameMappings = {
    "First Name": "firstName",
    "Last Name": "lastName",
    "Reference No": "referenceNo",
    Institute: "instituteName",
    Address: "address",
    Pincode: "pincode",
    Province: "province",
    Country: "country",
  };

  const getFieldName = (name) => fieldNameMappings[name] || name;

  const handleCopy = (copiedText) => {
    if (typeof copiedText === "object") {
      navigator.clipboard.writeText(JSON.stringify(copiedText?.props?.data));
      notify("Content Copied", "success");
    } else {
      navigator.clipboard.writeText(copiedText);
      notify("Content Copied", "success");
    }
  };

  function createData(name, code) {
    return {
      name,
      code,
      copy: (
        <ContentCopyIcon
          sx={{ cursor: "pointer" }}
          onClick={() => handleCopy(code)}
        />
      ),
    };
  }

  const extractDoctorDetails = (doctorData) => {
    const obj = {
      "Reference No": doctorData?.myReferenceNumber,
      "First Name": doctorData?.local?.firstName,
      "Last Name": doctorData?.local?.lastName,
      Gender: doctorData?.local?.gender,
      Email: doctorData?.local?.email,
      Institute: doctorData?.local?.instituteName,
      Address: doctorData?.local?.address,
      Pincode: doctorData?.local?.pincode,
      Province: doctorData?.local?.province,
      Country: doctorData?.local?.country,
      proofUri: doctorData?.proofUri,
      requestType: doctorData?.requestType,
      createdBy: doctorData?.createdBy,
      isVerified: doctorData?.accountVerified?.isVerified ? "True" : "False",
      verifiedOn: doctorData?.accountVerified?.verifiedOn,
      isBlocked: doctorData?.blockStatus?.isBlocked ? "True" : "False",
      blockedOn: doctorData?.blockStatus?.blockedOn || "N/A", // Assuming there's a blockedOn field for the date
      patientList: <JsonDisplay data={doctorData?.vision?.visionPatientList || {}} />,
      BookingHistory: <JsonDisplay data={doctorData?.vision?.visionBookingHistory || {}} />,
      visionPostpaidIsViewable: doctorData?.permissions?.visionPostpaid?.isViewable ? "True" : "False",
      visionPostpaidIsEnabled: doctorData?.permissions?.visionPostpaid?.isEnabled ? "True" : "False",
      visionPostpaidEnabledOn: doctorData?.permissions?.visionPostpaid?.enabledOn || "N/A",
      visionPostpaidEnabledBy: doctorData?.permissions?.visionPostpaid?.enabledBy || "N/A",
      chartPortalPayer: doctorData?.permissions?.chartPortal?.payer || "N/A",
      chartPortalSubscriptionId: doctorData?.permissions?.chartPortal?.subscriptionId || "N/A",
      chartPortalStatus: doctorData?.permissions?.chartPortal?.status || "N/A",
      chartPortalPlanId: doctorData?.permissions?.chartPortal?.planId || "N/A",
      chartPortalCurrentPeriodStart: doctorData?.permissions?.chartPortal?.currentPeriodStart || "N/A",
      chartPortalCurrentPeriodEnd: doctorData?.permissions?.chartPortal?.currentPeriodEnd || "N/A",
      chartPortalSubscriptionHistory: <JsonDisplay data={doctorData?.permissions?.chartPortal?.subscriptionHistory || {}} />,
      payUser: <JsonDisplay data={doctorData?.payUser || {}} />,
      created_at: doctorData?.created_at,
      updated_at: doctorData?.updated_at,
    };

    const formattedArray = Object.entries(obj).map(([key, value]) => ({
      name: key,
      code: value,
    }));

    return formattedArray;
  };

  const handleInputChange = ({ target }) => {
    const { name, value } = target;
    setEditedDoctorDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const isReactComponent = (obj) => React.isValidElement(obj);

  useEffect(() => {
    const updatedRows = isEditing
      ? doctorsArray?.map(({ name, code }) => {
          // this is for non editable fields
          if (
            name === "Gender" ||
            name === "Email" ||
            name === "Reference No" ||
            name === "created_at" ||
            name === "updated_at" ||
            name === "createdBy" || 
            name === "patientList" ||
            name === "BookingHistory" || 
            name === "visionPostPayment" ||
            name === "visionPostpaidIsViewable" || 
            name === "visionPostpaidIsEnabled" || 
            name === "visionPostpaidEnabledOn" ||
            name === "visionPostpaidEnabledBy" ||
            name === "chartPortalPayer" ||
            name === "chartPortalSubscriptionId" ||
            name === "chartPortalStatus" ||
            name === "chartPortalPlanId" ||
            name === "chartPortalCurrentPeriodStart" ||
            name === "chartPortalCurrentPeriodEnd" ||
            name === "chartPortalSubscriptionHistory" ||
            name === "payUser"
          ) {
            return createData(name, code);
          } else {
            return createData(
              name,
              <CustomInput
                value={
                  editedDoctorDetails[getFieldName(name)] !== undefined
                    ? editedDoctorDetails[getFieldName(name)]
                    : isReactComponent(code)
                      ? code.props.data
                      : code
                }
                name={getFieldName(name)}
                onChange={handleInputChange}
              />
            );
          }
        })
      : doctorsArray?.map(({ name, code }) => {
          return createData(name, code);
        });
    setRows(updatedRows);
  }, [isEditing, doctorsArray, editedDoctorDetails]);

  useEffect(() => {
    if (doctorData) {
      const formattedArray = extractDoctorDetails(doctorData);
      setDoctorsArray(formattedArray);
    }
  }, [doctorData]);

  const columns = [
    { id: "name", label: "Field Name", minWidth: 170 },
    { id: "code", label: "Field Value", minWidth: 170 },
    {
      id: "copy",
      label: "Copy",
      minWidth: 85,
      align: "left",
      format: (value) => value.toFixed(2),
    },
  ];

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleOnConfirm = () => {
    handleUpdateDoctor(doctorData?._id, {
      local: {
        ...doctorData?.local,
        ...editedDoctorDetails,
        patientList: undefined, // Explicitly exclude patientList from the local object
        proofUri: undefined,
        requestType: undefined,
        isVerified: undefined,
        verifiedOn: undefined,
        isBlocked: undefined,
        blockedOn: undefined,
        created_at: undefined,
        updated_at: undefined,
      },
      accountVerified: {
        ...doctorData?.accountVerified,
        isVerified: editedDoctorDetails?.isVerified
          ? editedDoctorDetails?.isVerified
          : doctorData?.accountVerified?.isVerified,
        verifiedOn: editedDoctorDetails?.verifiedOn
          ? editedDoctorDetails?.verifiedOn
          : doctorData?.accountVerified?.verifiedOn,
      },
      blockStatus: {
        ...doctorData?.blockStatus,
        isBlocked: editedDoctorDetails?.isBlocked
          ? editedDoctorDetails?.isBlocked
          : doctorData?.blockStatus?.isBlocked,
        blockedOn: editedDoctorDetails?.blockedOn
          ? editedDoctorDetails?.blockedOn
          : doctorData?.blockStatus?.blockedOn,
      },
      vision: {
        ...doctorData?.vision
      },
      proofUri: editedDoctorDetails?.proofUri
        ? editedDoctorDetails?.proofUri
        : doctorData?.proofUri,
      requestType: editedDoctorDetails?.requestType
        ? editedDoctorDetails?.requestType
        : doctorData?.requestType,
      myReferenceNumber: doctorData?.myReferenceNumber,
      createdBy: doctorData?.createdBy,
    });
    handlePopupCenterOpen(false);
  };

  const handleCancelConfirm = () => {
    handlePopupCenterOpen(false);
    setEditedDoctorDetails({});
  };

  const handleSave = () => {
    setIsEditing(false);
    handlePopupCenterOpen(true);
    handlePopupCenterComponentRender(
      <ConfirmDetailsPopup
        details={editedDoctorDetails}
        onConfirm={handleOnConfirm}
        onClose={handleCancelConfirm}
      />
    );
  };

  const handleNavigateDashboard = () => {
    navigate("/");
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditedDoctorDetails({});
  };

  const handleNavigateDoctor = () => {
    navigate("/managedoctor");
  };

  // const convertArrayToCSV = (data) => {
  //   const csvRows = [];
  //   // Get the headers
  //   const headers = Object.keys(data[0]);
  //   csvRows.push(headers.join(","));

  //   // Loop over the rows
  //   for (const row of data) {
  //     const values = headers.map((header) => {
  //       const escaped = ("" + row[header]).replace(/"/g, '\\"');
  //       return `"${escaped}"`;
  //     });
  //     csvRows.push(values.join(","));
  //   }

  //   return csvRows.join("\n");
  // };

  const convertArrayToCSV = (data) => {
    const csvRows = [];
    // Get the headers
    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(","));

    // Loop over the rows
    for (const row of data) {
      const values = headers.map((header) => {
        const value = row[header];
        let escaped;
        if (typeof value === "object" && value !== null) {
          escaped = JSON.stringify(value?.props?.data);
        } else {
          escaped = ("" + value).replace(/"/g, '\\"');
        }
        return `"${escaped}"`;
      });
      csvRows.push(values.join(","));
    }

    return csvRows.join("\n");
  };

  const downloadCSV = () => {
    const csvData = convertArrayToCSV(doctorsArray);
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", `doctor:${doctorData?.local?.email}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className={styles.Wrapper}>
      <div className={styles.BreadCrumb}>
        <div className={styles.NavigationWrapper}>
          <Home className={styles.HomeIcon} onClick={handleNavigateDashboard} />{" "}
          <div>
            <span
              className={styles.DashboardText}
              onClick={handleNavigateDashboard}
            >
              Dashboard
            </span>
            <span
              className={`${styles.RouteText} ${styles.CursorPointer}`}
              onClick={handleNavigateDoctor}
            >
              {" "}
              /Doctor
            </span>
            <span className={styles.RouteText}>
              {" "}
              /{doctorData?.myReferenceNumber}
            </span>
          </div>
        </div>
        <div className={styles.BtnWrapper}>
          {width > 600 ? (
            <Button
              text={"Export to CSV"}
              btnType={"secondary"}
              onClick={downloadCSV}
            />
          ) : null}
          {isEditing ? (
            <>
              <Button btnType={"primary"} text={"Save"} onClick={handleSave} />
              <Button
                btnType={"secondary"}
                text={"Cancel"}
                onClick={handleEditCancel}
              />
            </>
          ) : (
            <Button btnType={"secondary"} text={"Edit"} onClick={handleEdit} />
          )}
        </div>
      </div>
      <DetailTableComponent rows={rows} columns={columns} />
      {width > 600 ? null : (
        <Button
          text={"Export to CSV"}
          btnType={"secondary"}
          className={styles.ExportCsvBtn}
          onClick={downloadCSV}
        />
      )}
    </div>
  );
};

export default DoctorDetailComponent;
